/* eslint-disable @typescript-eslint/no-explicit-any */
import { DependencyList, useContext, useCallback } from "react";

import { captureException } from "@sentry/react";
import dateformat from "dateformat";

import { getAddresses, getUserAddress } from "api/address";
import {
  GetCandidateRoutesParams,
  getCandidateRoutes,
} from "api/candidate_routes";
import {
  deleteMovementLog,
  createTransportationExpense,
  updateMovementLogApprovalStatus,
} from "api/movement_logs";
import { updatePreparedRouteExpirationDate } from "api/prepared_routes";
import { CustomerAPIClient } from "api_clients";
import { isApiError } from "api_clients/api_error";
import { APPLICATION_STATUS } from "enums/applicationStatus";
import { kilometerToMeter } from "helpers/distance";
import { useErrorContext } from "hooks/error";
import Address from "models/address";
import { NewMovementLog } from "models/movementLog";
import { Paginate } from "models/paginate";
import {
  SuggestedPreparedRoute,
  SuggestedPreparedRouteDetail,
} from "models/suggestedPreparedRoute";
import { AuthContext } from "providers/CognitoAuthProvider";

export const useCallbackWithErrorHandling = (
  callback: (...args: any[]) => Promise<any>,
  deps: DependencyList
): ((...args: any[]) => Promise<any>) => {
  const { dispatch } = useErrorContext();

  return useCallback(
    (...args: any[]) => {
      const f = async () => {
        try {
          return await callback(...args);
        } catch (error) {
          if (!isApiError(error)) throw error;
          captureException(error);
          dispatch({ type: "error:apiError", payload: { error } });
          return Promise.resolve();
        }
      };
      return f();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps
  );
};

export const useGetCandidateRoutesApi = (): ((
  params: GetCandidateRoutesParams
) => Promise<SuggestedPreparedRoute[]>) => {
  const auth = useContext(AuthContext);

  return useCallbackWithErrorHandling(
    (params) => getCandidateRoutes(auth, params),
    [auth]
  );
};

export const useGetAddressesApi = (): ((
  workplaceName: string,
  page: number | null
) => Promise<Paginate<Address>>) => {
  const auth = useContext(AuthContext);
  return useCallbackWithErrorHandling(
    (workplaceName, page) => getAddresses(auth, workplaceName, page),
    [auth]
  );
};

export const useGetUserAddressApi = (): (() => Promise<Address>) => {
  const auth = useContext(AuthContext);
  return useCallbackWithErrorHandling(() => getUserAddress(auth), [auth]);
};

export const useGetApproverPreparedRouteApprovalsApi =
  (): (() => Promise<any>) => {
    const auth = useContext(AuthContext);
    return useCallbackWithErrorHandling(
      () =>
        CustomerAPIClient.get(auth, "/api/approver/prepared_route_approvals"),
      [auth]
    );
  };

export const useUpdateApproverPreparedRouteApprovalApi = (): ((
  id: number,
  status: keyof typeof APPLICATION_STATUS,
  formattedComment: string
) => Promise<any>) => {
  const auth = useContext(AuthContext);

  return useCallbackWithErrorHandling(
    (id, status, formattedComment) =>
      CustomerAPIClient.update(
        auth,
        `/api/approver/prepared_route_approvals/${id}`,
        {
          prepared_route_approval: {
            approval_status: status,
            approver_comment: formattedComment,
          },
        }
      ),
    [auth]
  );
};

export const useUpdateApproverMovementLogApprovalApi = (): ((
  id: number,
  status: keyof typeof APPLICATION_STATUS,
  formattedComment: string
) => Promise<any>) => {
  const auth = useContext(AuthContext);

  return useCallbackWithErrorHandling(
    (id, status, formattedComment) =>
      CustomerAPIClient.update(
        auth,
        `/api/approver/movement_log_approvals/${id}`,
        {
          movement_log_approval: {
            approval_status: status,
            approver_comment: formattedComment,
          },
        }
      ),
    [auth]
  );
};

export const useGetPreparedRoutesApi = (): (() => Promise<any>) => {
  const auth = useContext(AuthContext);

  return useCallbackWithErrorHandling(
    () => CustomerAPIClient.get(auth, "/api/prepared_routes"),
    [auth]
  );
};

export const usePostPreparedRoutesApi = (): ((
  preparedRoute: {
    departureId: number;
    arrivalId: number;
    type: "public_transportation" | "my_car";
    details: SuggestedPreparedRouteDetail[];
  },
  deriveFareFn: (detail: SuggestedPreparedRouteDetail) => number | undefined
) => Promise<any>) => {
  const auth = useContext(AuthContext);

  return useCallbackWithErrorHandling(
    ({ departureId, arrivalId, type, details }, deriveFareFn) =>
      CustomerAPIClient.post(auth, "/api/prepared_routes", {
        prepared_route: {
          start_date: dateformat(new Date(), "yyyy-mm-dd"),
          end_date: "9999-12-31",
          departure_id: departureId,
          arrival_id: arrivalId,
          type,
          prepared_route_details: details.reduce(
            (acc: any[], detail: SuggestedPreparedRouteDetail) => [
              ...acc,
              {
                transportation_type: detail.transportationType,
                order_no: detail.orderNo,
                departure_id: detail.departureId,
                departure_name: detail.departureName
                  ? detail.departureName
                  : "",
                arrival_id: detail.arrivalId,
                arrival_name: detail.arrivalName ? detail.arrivalName : "",
                line_id: detail.lineId,
                line_name: detail.lineName,
                fare: deriveFareFn(detail),
                time_: detail.time,
                distance: kilometerToMeter(detail.distanceKm),
                use_commuter_pass: detail.useCommuterPass,
              },
            ],
            []
          ),
        },
      }),
    [auth]
  );
};

export const useUpdatePreparedRoutePriorityApi = (): ((
  id: number
) => Promise<any>) => {
  const auth = useContext(AuthContext);

  return useCallbackWithErrorHandling(
    (id) =>
      CustomerAPIClient.update(auth, `/api/prepared_routes/${id}`, {
        priority: 1,
      }),
    [auth]
  );
};

export const useUpdatePreparedRouteExpirationDateApi = (): ((
  id: number,
  startDate: Date,
  endDate: Date
) => Promise<any>) => {
  const auth = useContext(AuthContext);
  const updateApi = updatePreparedRouteExpirationDate;
  return useCallbackWithErrorHandling(
    (id, startDate, endDate) => updateApi(auth, id, startDate, endDate),
    [auth]
  );
};

export const useDelPreparedRouteApi = (): ((id: number) => Promise<any>) => {
  const auth = useContext(AuthContext);

  return useCallbackWithErrorHandling(
    (id) => CustomerAPIClient.del(auth, `/api/prepared_routes/${id}`),
    [auth]
  );
};

export const useUpdatePreparedRoutesPreparedRouteIdPreparedRouteApprovalsApi =
  (): ((
    preparedRouteId: number,
    status: keyof typeof APPLICATION_STATUS,
    formattedComment: string
  ) => Promise<any>) => {
    const auth = useContext(AuthContext);

    return useCallbackWithErrorHandling(
      (preparedRouteId, status, formattedComment) =>
        CustomerAPIClient.update(
          auth,
          `/api/prepared_routes/${preparedRouteId}/prepared_route_approvals`,
          {
            prepared_route_approval: {
              approval_status: status,
              applicant_comment: formattedComment,
            },
          }
        ),
      [auth]
    );
  };

export const useGetMovementLogsMovementLogIdMovementRoutesApi = (): ((
  movementLogId: number
) => Promise<any>) => {
  const auth = useContext(AuthContext);
  return useCallbackWithErrorHandling(
    (movementLogId) =>
      CustomerAPIClient.get(
        auth,
        `/api/movement_logs/${movementLogId}/movement_routes`
      ),
    [auth]
  );
};

export const useGetApproverMovementRoutesApi = (): ((
  userId: number,
  movementLogId: number
) => Promise<any>) => {
  const auth = useContext(AuthContext);
  return useCallbackWithErrorHandling(
    (userId, movementLogId) =>
      CustomerAPIClient.get(
        auth,
        `/api/approver/users/${userId}/movement_logs/${movementLogId}/movement_routes`
      ),
    [auth]
  );
};

export const useGetMovementLogsApi = (): ((
  monthString: string
) => Promise<any>) => {
  const auth = useContext(AuthContext);
  return useCallbackWithErrorHandling(
    (monthString) =>
      CustomerAPIClient.get(auth, `/api/movement_logs?month=${monthString}`),
    [auth]
  );
};

export const useGetApproverUsersUserIdMovementLogApi = (): ((
  userId: string,
  id: string
) => Promise<any>) => {
  const auth = useContext(AuthContext);
  return useCallbackWithErrorHandling(
    (userId, id) =>
      CustomerAPIClient.get(
        auth,
        `/api/approver/users/${userId}/movement_logs/${id}`
      ),
    [auth]
  );
};

export const useGetApproverUsersUserIdPreparedRouteApi = (): ((
  userId: string | undefined,
  id: string | undefined
) => Promise<any>) => {
  const auth = useContext(AuthContext);
  return useCallbackWithErrorHandling(
    (userId, id) =>
      CustomerAPIClient.get(
        auth,
        `/api/approver/users/${userId}/prepared_routes/${id}`
      ),
    [auth]
  );
};

export const useGetApproverUsersUserIdPreparedRoutesApi = (): ((
  userId: string | undefined
) => Promise<any>) => {
  const auth = useContext(AuthContext);
  return useCallbackWithErrorHandling(
    (userId) =>
      CustomerAPIClient.get(
        auth,
        `/api/approver/users/${userId}/prepared_routes`
      ),
    [auth]
  );
};

export const useDelMovementLogApi = (): ((id: number) => Promise<any>) => {
  const auth = useContext(AuthContext);
  return useCallbackWithErrorHandling(
    (id) => deleteMovementLog(auth, id),
    [auth]
  );
};

export const useCreateTransportationExpenseApi = (): ((
  movementLog: NewMovementLog
) => Promise<any>) => {
  const auth = useContext(AuthContext);
  return useCallbackWithErrorHandling(
    (movementLog) => createTransportationExpense(auth, movementLog),
    [auth]
  );
};

export const useUpdateMovementLogApprovalStatusApi = (): ((
  id: number,
  status: keyof typeof APPLICATION_STATUS,
  formattedComment: string
) => Promise<any>) => {
  const auth = useContext(AuthContext);
  return useCallbackWithErrorHandling(
    (id, status, formattedComment) =>
      updateMovementLogApprovalStatus(auth, id, status, formattedComment),
    [auth]
  );
};

export const useGetMovementLogApprovalsApi = (): ((
  monthString?: string | null
) => Promise<any>) => {
  const auth = useContext(AuthContext);
  return useCallbackWithErrorHandling(
    // CustomerAPIClient.get(auth, `/api/movement_logs?month=${monthString}`),
    (monthString) =>
      CustomerAPIClient.get(
        auth,
        `/api/approver/movement_log_approvals${
          monthString ? `?month=${monthString}` : ""
        }`
      ),
    [auth]
  );
};

/* eslint-enable @typescript-eslint/no-explicit-any */
